import React, { createContext, useContext, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { colors, media } from '../../../core';
import Icon from '../Icon';
import Modal from '../Modal';

export interface Variant {
  variant: 'primary' | 'secondary' | 'secondary-level' | 'third-level';
}

export interface LabelProps {
  children: React.ReactNode;
  padding?: string;
}

export interface SectionProps {
  children: React.ReactNode;
}

export interface AccordionProps extends Variant {
  children: React.ReactNode;
  visible: boolean;
  disabled?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  onEditClick?: () => void;
  closeModalMessage?: string;
  margin?: string;
  viewState?: 'edit' | 'complete';
}

export interface AccordionContextProps extends Variant {
  isVisible: boolean;
  handleClick?: () => void;
  handleEditClick?: () => void;
  closeModalMessage?: string;
  disabled?: boolean;
  clickable?: boolean;
  viewState?: 'edit' | 'complete';
}

export const AccordionContext = createContext<AccordionContextProps>({
  isVisible: false,
  variant: 'primary',
  disabled: false,
});

const StyledAccordion = styled.div<
  Variant & { isVisible: boolean; margin?: string; disabled?: boolean }
>`
  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.3;
  `}

  overflow: hidden;

  ${({ variant, margin, theme }) =>
    variant === 'primary' &&
    `
      border: 1px solid ${theme.accordionBorderColor || colors.cloud};
      border-radius:  ${theme.borderRadius || '3px'};
      padding: 0;
      margin: ${margin ? margin : 0};
  `}

  ${({ variant, isVisible, theme }) =>
    variant === 'secondary' &&
    `
      box-shadow: ${isVisible ? '0 2px 6px 0 rgba(233, 233, 233, 0.5)' : 'none'};
      border: 1px solid ${isVisible ? theme.accordionBorderColor || colors.cloud : 'transparent'};
      border-radius: ${theme.borderRadius || '3px'};
      padding-bottom: ${isVisible ? '14px' : 0};
      transition: 0.3s;

      ${media.tabletPortrait} {
        margin-top: -2px;
        background-color: ${isVisible ? colors.white : 'transparent'};
      }
  `}

  ${({ variant, isVisible }) =>
    variant === 'third-level' &&
    `
      border-bottom:  ${isVisible ? 'none' : '1px solid #3A3A3A'};;
      padding-bottom: ${isVisible ? '14px' : 0};
      transition: 0.3s;

      ${media.tabletPortrait} {
        margin-top: -2px;
        background-color: 'transparent';
      }
  `}
`;

const StyledLabel = styled.button<
  Variant & { open: boolean; padding?: string; disabled?: boolean; clickable?: boolean }
>`
  -webkit-appearance: none;
  background-color: transparent;
  border: 0;
  margin: 0;
  cursor: ${({ disabled, clickable }) => (disabled || !clickable ? 'auto' : 'pointer')};
  outline: none;
  display: flex;
  flex: 1;
  width: 100%;
  align-items: center;
  justify-content: space-between;

  &:focus {
    outline: none;
  }

  ${({ variant, padding, theme }) =>
    variant === 'primary' &&
    `
      background-color: ${theme.accordionPanelBackgorundColor || colors.pearl};
      padding: ${padding ? padding : '16px'};
  `}

  ${({ variant, open, padding, theme }) =>
    (variant === 'secondary' || variant === 'third-level') &&
    `
      display: flex;
      align-items: center;
      text-align: left;
      padding: ${padding ? padding : '16px 20px'};

      ${media.tabletPortrait} {
        padding: 16px 12px;
        border-bottom: 1px solid ${
          open ? 'transparent' : theme.accordionBorderColor || colors.cloud
        };
      }
  `}

  ${({ variant, padding }) =>
    variant === 'secondary-level' &&
    `
      display: flex;
      align-items: center;
      text-align: left;
      padding: ${padding ? padding : '4px 0'};
  `}
`;

const RightIcon = styled.div<{ open: boolean }>`
  display: flex;
  transition: transform 0.3s;
  transform: ${({ open }) => (open ? 'rotate(-180deg)' : 'rotate(0)')};
`;

const Content = styled.div<{ open: boolean } & Variant>`
  opacity: ${({ open }) => (open ? '1' : '0')};
  max-height: ${({ open }) => (open ? '100%' : '0')};
  overflow: hidden;
  position: relative;
`;

const EditButton = styled.button`
  position: absolute;
  right: 6px;
  top: 6px;
  align-self: flex-end;
  cursor: pointer;
  border: 0;
  background: none;
  outline: none;
  -webkit-appearance: none;
  padding: 10px;
  z-index: 1;
`;

const StyledIcon = styled(Icon)`
  cursor: pointer;
`;

const Label = ({ children, padding }: LabelProps) => {
  const { isVisible, handleClick, disabled, clickable, variant }: AccordionContextProps =
    useContext(AccordionContext);
  const theme = useTheme();

  if (disabled) {
    return (
      <StyledLabel
        disabled={disabled}
        variant={variant}
        onClick={handleClick}
        open={isVisible}
        padding={padding}
      >
        {children}
      </StyledLabel>
    );
  }

  return (
    <StyledLabel
      variant={variant}
      onClick={handleClick}
      open={isVisible}
      padding={padding}
      clickable={clickable}
    >
      {children}
      {clickable && (
        <React.Fragment>
          {variant === 'primary' ? (
            <StyledIcon
              type={isVisible ? 'minus' : 'plus'}
              color={theme.iconColor || colors.primary}
            />
          ) : (variant === 'secondary' || variant === 'third-level') ? (
            <RightIcon open={isVisible}>
              <StyledIcon
                type={theme.accordionDropDownIcon.type || 'caret-down'}
                color={theme.iconColor || colors.primary}
                size={theme.accordionDropDownIcon.viewSize || 20}
                viewSize={theme.accordionDropDownIcon.viewSize || 20}
              />
            </RightIcon>
          ) : (
            <RightIcon open={isVisible}>
              <StyledIcon
                type={theme.accordionDropDownIcon.type || 'caret-down'}
                size={16}
                viewSize={theme.accordionDropDownIcon.viewSize || 20}
                color={theme.iconColor || colors.primary}
              />
            </RightIcon>
          )}
        </React.Fragment>
      )}
    </StyledLabel>
  );
};

const Section = ({ children }: SectionProps) => {
  const theme = useTheme();
  const {
    isVisible,
    variant,
    handleEditClick,
    closeModalMessage,
    viewState,
    disabled,
  }: AccordionContextProps = useContext(AccordionContext);
  const [isShowingModal, setShowingModal] = useState(false);

  const showEditButton = handleEditClick && viewState === 'complete';

  const edit = () => {
    if (closeModalMessage && viewState === 'edit') {
      setShowingModal(true);
    } else {
      handleEditClick && handleEditClick();
    }
  };

  const handleConfirm = () => {
    setShowingModal(false);

    handleEditClick && handleEditClick();
  };

  return (
    <Content open={isVisible} variant={variant}>
      {showEditButton && !disabled && (
        <EditButton onClick={edit}>
          <Icon color={theme.iconColor || colors.primary} type="edit" onClick={edit} />
        </EditButton>
      )}

      {children}

      {isShowingModal && (
        <Modal
          width="500px"
          title="Confirmation"
          showButtons
          onPress={handleConfirm}
          onClose={() => setShowingModal(false)}
        >
          {closeModalMessage}
        </Modal>
      )}
    </Content>
  );
};

const Accordion = ({
  children,
  visible,
  variant,
  disabled,
  clickable = true,
  margin,
  onClick,
  onEditClick,
  viewState,
  closeModalMessage,
}: AccordionProps) => {
  const handleClick = () => {
    onClick && onClick();
  };

  const handleEditClick = () => {
    onEditClick && onEditClick();
  };

  return (
    <AccordionContext.Provider
      value={{
        isVisible: visible,
        handleClick,
        handleEditClick,
        viewState,
        variant,
        closeModalMessage,
        disabled,
        clickable,
      }}
    >
      <StyledAccordion variant={variant} isVisible={visible} margin={margin} disabled={disabled}>
        {children}
      </StyledAccordion>
    </AccordionContext.Provider>
  );
};

Accordion.Label = Label;
Accordion.Section = Section;

export default Accordion;
